import {
  formatPrice,
} from 'common/formatters';

import {
  PARTNER_COLLECT_FIRST_MONTH_TIME_SECURITY_DEPOSIT_PAYMENT,
  PARTNER_COLLECT_FIRST_MONTH_TIME_MOVE_IN,
} from 'common/partnerConstants';

import {
  getLeaseTemplates,
} from 'common/leaseDrafting/leaseTemplates';

import {
  getSecurityDepositSettings,
} from 'common/listingHelpers';

import {
  showFieldIf,
} from 'client/helpers/formikHelpers';

import {
  LISTING_AGREEMENT_CUSTOM,
  LISTING_AGREEMENT_OPTION_ITEMS,
  LISTING_PROPERTY_OWNER_BUSINESS,
  LISTING_PROPERTY_OWNER_OPTION_ITEMS,
  LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL,
  LISTING_PROPERTY_OWNER_INDIVIDUAL,
} from 'common/listingConstants';

import {
  SR_CHECKBOX_THEME_BOX,
} from 'client/components/Checkbox';

const makeFormFieldsGetterFunc = ({
  partner,
  listing,
  securityDepositLabel,
  householdListing,
  householdApplications,
  householdGuarantors,
}) => {
  return (formikBag) => {
    const {
      values,
      setFieldValue,
    } = formikBag;

    const {
      firstMonthRentEnabled,
      firstMonthRentCollectionTimeEnabled,
      propertyOwnerNameEnabled,
      propertyOwnerPhoneEnabled,
      propertyOwnerEmailEnabled,
      managementEnabled,
      managementNameEnabled,
      managementEmailEnabled,
      managementPhoneEnabled,
      repairContactEnabled,
      repairContactNameEnabled,
      repairContactEmailEnabled,
      repairContactPhoneEnabled,
    } = partner.experienceConfiguration;

    const { whoCollectsSecurityDepositPetFees } = getSecurityDepositSettings({
      listing,
      partner,
    });

    const shouldShowCollectFirstMonthQuestion = (
      firstMonthRentEnabled
      && whoCollectsSecurityDepositPetFees !== LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL
    );

    const formFields = [
      {
        id: 'securityDepositIncreaseMultiplier',
        title: 'Amount',
        type: 'multiple_choice',
        properties: {
          choices: [
            1, 1.5, 2,
          ].map((multiplier) => ({
            label: `${formatPrice(listing.securityDeposit * multiplier)} (${multiplier}x)`,
            value: multiplier,
          })),
          multipleSelection: false,
          fieldType: SR_CHECKBOX_THEME_BOX,
          autoFitWidth: '215px',
          noMargin: true,
        },
        validations: ['required'],
      },
      showFieldIf(values.securityDepositIncreaseMultiplier > 1, {
        id: 'securityDepositIncreaseReason',
        title: `Provide a reason for increasing the ${securityDepositLabel}.`,
        subtitle: 'This explanation will be shared directly with the renters, to help get the deal done.',
        type: 'long_text',
        validations: ['required'],
      }),
      {
        id: 'rentPrice',
        title: 'Confirm rent price',
        subtitle: (
          'This is not prorated rent. We will use your rent price to calculate the prorated rent later.'
        ),
        type: 'currency',
        validations: ['required', 'positiveNumber'],
        properties: {
          noMargin: true,
        },
      },
      showFieldIf(shouldShowCollectFirstMonthQuestion, {
        id: 'collectFirstMonth',
        title: 'Would you like to collect 1st month\'s rent through Sunroom?',
        type: 'yes_no',
        validations: ['required'],
      }),
      showFieldIf(firstMonthRentCollectionTimeEnabled && values.collectFirstMonth, {
        id: 'collectFirstMonthTime',
        title: 'When would you like to collect first month\'s rent?',
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          useArrayValues: false,
          fieldType: SR_CHECKBOX_THEME_BOX,
          choices: [
            {
              label: `Collect with ${securityDepositLabel}`,
              value: PARTNER_COLLECT_FIRST_MONTH_TIME_SECURITY_DEPOSIT_PAYMENT,
            },
            {
              label: 'Collect at move-in',
              value: PARTNER_COLLECT_FIRST_MONTH_TIME_MOVE_IN,
            },
          ],
          autoFitWidth: '400px',
        },
        validations: ['required'],
      }),
      {
        id: 'listingAgreementType',
        title: 'How would you like to give us the leasing info we need?',
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          useArrayValues: false,
          fieldType: SR_CHECKBOX_THEME_BOX,
          choices: LISTING_AGREEMENT_OPTION_ITEMS,
          autoFitWidth: '400px',
        },
        validations: ['required'],
      },
      showFieldIf(values.listingAgreementType === LISTING_AGREEMENT_CUSTOM, {
        id: 'listingAgreementUrl',
        title: 'Upload Listing Agreement',
        type: 'file_single',
        properties: {
          fileAccept: '.pdf',
          filePath: `/listing-agreement/listing-${listing.id}-{uuid}`,
          successMessage: 'File uploaded!',
        },
        validations: ['required'],
      }),
      {
        id: 'propertyOwnerType',
        title: 'Who is the property owned by?',
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          useArrayValues: false,
          fieldType: SR_CHECKBOX_THEME_BOX,
          choices: LISTING_PROPERTY_OWNER_OPTION_ITEMS,
          autoFitWidth: '400px',
          onChange: (propertyOwnerType) => {
            setFieldValue('propertyOwnerType', propertyOwnerType);
            setFieldValue('propertyOwnerName', '');
            setFieldValue('propertyOwnerPhone', '');
            setFieldValue('propertyOwnerEmail', '');
            setFieldValue('isManagementSameAsPropertyOwner', '');
            setFieldValue('managementName', '');
            setFieldValue('managementEmail', '');
            setFieldValue('managementPhone', '');
            setFieldValue('isRepairSameAsManagement', '');
            setFieldValue('repairContactName', '');
            setFieldValue('repairContactEmail', '');
            setFieldValue('repairContactPhone', '');
          },
        },
        validations: ['required'],
      },
      showFieldIf(!!values.propertyOwnerType && propertyOwnerNameEnabled, {
        id: 'propertyOwnerName',
        title: values.propertyOwnerType === LISTING_PROPERTY_OWNER_BUSINESS
          ? `${values.propertyOwnerType}: Name`
          : `${values.propertyOwnerType}: Full name`,
        type: 'short_text',
        properties: {
          placeholder: values.propertyOwnerType === LISTING_PROPERTY_OWNER_BUSINESS
            ? 'Name of the business'
            : 'Full name',
        },
        validations: ['required'],
      }),
      showFieldIf(!!values.propertyOwnerType && propertyOwnerEmailEnabled, {
        id: 'propertyOwnerEmail',
        title: `${values.propertyOwnerType}: Email`,
        type: 'email',
        properties: {
          placeholder: 'Email address',
        },
        validations: ['required', 'email'],
      }),
      showFieldIf(!!values.propertyOwnerType && propertyOwnerPhoneEnabled, {
        id: 'propertyOwnerPhone',
        title: `${values.propertyOwnerType}: Phone`,
        type: 'phone',
        properties: {
          placeholder: '(xxx)-xxx-xxxx',
        },
        validations: ['required', 'phone'],
      }),
      showFieldIf(values.propertyOwnerType === LISTING_PROPERTY_OWNER_INDIVIDUAL && managementEnabled, {
        id: 'isManagementSameAsPropertyOwner',
        title: 'Does the individual manage the property?',
        type: 'yes_no',
        validations: ['required'],
      }),
      showFieldIf(values.isManagementSameAsPropertyOwner === false && managementNameEnabled, {
        id: 'managementName',
        title: 'Management: Name',
        type: 'short_text',
        properties: {
          placeholder: 'Full name',
        },
        validations: ['required'],
      }),
      showFieldIf(values.isManagementSameAsPropertyOwner === false && managementEmailEnabled, {
        id: 'managementEmail',
        title: 'Management: Email',
        type: 'email',
        properties: {
          placeholder: 'Email address',
        },
        validations: ['required', 'email'],
      }),
      showFieldIf(values.isManagementSameAsPropertyOwner === false && managementPhoneEnabled, {
        id: 'managementPhone',
        title: 'Management: Phone',
        type: 'phone',
        properties: {
          placeholder: '(xxx)-xxx-xxxx',
        },
        validations: ['required', 'phone'],
      }),
      showFieldIf(!!values.propertyOwnerType && repairContactEnabled, {
        id: 'isRepairSameAsManagement',
        title: 'Is the repair contact the same as the management contact?',
        type: 'yes_no',
        validations: ['required'],
      }),
      showFieldIf(values.isRepairSameAsManagement === false && repairContactNameEnabled, {
        id: 'repairContactName',
        title: 'Repair contact name',
        type: 'short_text',
        properties: {
          placeholder: 'Full name',
        },
        validations: ['required'],
      }),
      showFieldIf(values.isRepairSameAsManagement === false && repairContactEmailEnabled, {
        id: 'repairContactEmail',
        title: 'Repair contact email',
        type: 'email',
        properties: {
          placeholder: 'Email address',
        },
        validations: ['required', 'email'],
      }),
      showFieldIf(values.isRepairSameAsManagement === false && repairContactPhoneEnabled, {
        id: 'repairContactPhone',
        title: 'Repair contact phone',
        type: 'phone',
        properties: {
          placeholder: '(xxx)-xxx-xxxx',
        },
        validations: ['required', 'phone'],
      }),
      {
        id: 'additionalAddenda',
        type: 'multiple_choice',
        properties: {
          fieldType: SR_CHECKBOX_THEME_BOX,
          choices: getLeaseTemplates({
            applications: householdApplications,
            guarantors: householdGuarantors,
            householdListing,
            listing,
          }).filter((document) => {
            return document.isAddenda && !document.enabled;
          }).map((document) => ({
            label: document.title,
            value: document.title,
          })),
          autoFitWidth: '400px',
          checkboxClassName: 'leftAlign',
        },
      },
      {
        id: 'whoCollectsRentPayment',
        title: 'Who will collect rent payments?',
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          useArrayValues: false,
          fieldType: SR_CHECKBOX_THEME_BOX,
          choices: [
            {
              label: 'Owner',
              value: 'Owner',
            },
            {
              label: 'Property Manager',
              value: 'Property Manager',
            },
          ],
          autoFitWidth: '400px',
        },
        validations: ['required'],
      },
      {
        id: 'rentPaymentDescription',
        title: 'How will the renter pay rent?',
        type: 'short_text',
        properties: {
          placeholder: 'ie. Direct Deposit, Venmo, Cash',
        },
        validations: ['required'],
      },
      {
        id: 'partnerLeaseNotes',
        title: 'Extra notes',
        titleSecondary: '(optional)',
        type: 'long_text',
        properties: {
          placeholder: 'Anything else you’d like to share?',
        },
      },
    ];

    return formFields;
  };
};

export default makeFormFieldsGetterFunc;
